import { useState } from 'react';
import { api } from '../api.ts';
import { msg } from '../app/actions.ts';
import { useNav } from '../app/nav.tsx';
import { useToast } from '../app/toast.tsx';
import { AN_CARE, AN_LVL, AN_STEPS, type Answers } from '../lib/anamnese.ts';
import { useCash, useClient, useMe, useRefresh } from '../lib/data.ts';
import { first, money, parseMoney } from '../lib/format.ts';
import { Gr } from '../ui/bits.tsx';
import { Skeleton } from '../ui/State.tsx';
import { Icon } from '../ui/Icon.tsx';
import { Sheet } from '../ui/Sheet.tsx';

// cliente nova ou edição dos dados
export function EditCliSheet({ clientId, name: preset }: { clientId?: string; name?: string }) {
  const nav = useNav();
  const toast = useToast();
  const refresh = useRefresh();
  const c = useClient(clientId).data;
  const [f, setF] = useState<{ name: string; phone: string; bday: string; whats: boolean; notes: string } | null>(clientId ? null : { name: preset ?? '', phone: '', bday: '', whats: false, notes: '' });
  if (clientId && !c) return <Sheet title="Editar cliente"><Skeleton kind="sheet" rows={4} /></Sheet>;
  const v = f ?? { name: c!.name, phone: c!.phoneDisplay, bday: c!.birthday ? `${c!.birthday.slice(-2)}/${c!.birthday.slice(-5, -3)}` : '', whats: !!c!.whatsappConsentAt, notes: c!.notes };
  const set = (k: keyof typeof v, val: string | boolean) => setF({ ...v, [k]: val });
  const digits = v.phone.replace(/\D/g, '');
  const bdayOk = !v.bday || /^\d{2}\/\d{2}$/.test(v.bday);
  const ok = v.name.trim().length > 1 && (digits.length === 0 || digits.length >= 10) && bdayOk;
  const isNew = !clientId;
  async function save() {
    const birthday = v.bday ? (c?.birthday && /^\d{4}/.test(c.birthday) ? `${c.birthday.slice(0, 4)}-${v.bday.slice(3)}-${v.bday.slice(0, 2)}` : `--${v.bday.slice(3)}-${v.bday.slice(0, 2)}`) : null;
    try {
      if (isNew) {
        const n = await api.post<{ id: string }>('/clients', { name: v.name.trim(), phone: v.phone, birthday, notes: v.notes });
        if (v.whats) await api.post(`/clients/${n.id}/consent`, { whatsapp: true });
        await refresh(); toast(`${first(v.name)} cadastrada.`); nav.close();
      } else {
        await api.put(`/clients/${clientId}`, { name: v.name.trim(), phone: v.phone, birthday, notes: v.notes, email: c!.email, instagram: c!.instagram, version: c!.version });
        const lost = !!c!.whatsappConsentAt && !v.whats;
        if (!!c!.whatsappConsentAt !== v.whats) await api.post(`/clients/${clientId}/consent`, { whatsapp: v.whats });
        await refresh(); toast(lost ? `${first(v.name)} não recebe mais mensagens pelo WhatsApp.` : `Dados da ${first(v.name)} atualizados.`); nav.close();
      }
    } catch (e) { toast(msg(e), 'warn'); }
  }
  return (
    <Sheet title={isNew ? 'Cliente nova' : 'Editar cliente'} sub={isNew ? 'Sem agendar agora' : c!.name}
      actions={<><button className="cta" disabled={!ok} onClick={save}>Salvar</button><button className="link-back" onClick={nav.close}>Cancelar</button></>}>
      <div className="field"><label htmlFor="ecName">Nome</label><input className="text-in" id="ecName" autoFocus autoComplete="off" value={v.name} onChange={(e) => set('name', e.target.value)} /></div>
      <div className="field"><label htmlFor="ecPhone">Celular</label><input className="text-in" id="ecPhone" type="tel" inputMode="tel" autoComplete="off" value={v.phone} onChange={(e) => set('phone', e.target.value)} placeholder="(48) 90000-0000" /></div>
      <div className="field"><label htmlFor="ecBday">Aniversário</label><input className="text-in" id="ecBday" inputMode="numeric" autoComplete="off" placeholder="dd/mm" maxLength={5} value={v.bday}
        onChange={(e) => { const d = e.target.value.replace(/\D/g, '').slice(0, 4); set('bday', d.length > 2 ? `${d.slice(0, 2)}/${d.slice(2)}` : d); }} /></div>
      <div className="field"><label htmlFor="ecNotes">Preferências</label><input className="text-in" id="ecNotes" autoComplete="off" value={v.notes} onChange={(e) => set('notes', e.target.value)} placeholder="Ex.: prefere tons nude, horários de manhã" /></div>
      <div className="toggle-line"><span><strong>Autorizou mensagens no WhatsApp</strong><small>{v.whats ? 'Recebe lembretes e confirmações.' : 'Não recebe nenhuma mensagem automática.'}</small></span>
        <button role="switch" aria-checked={v.whats} aria-label="Autorizou mensagens no WhatsApp" onClick={() => set('whats', !v.whats)}><Icon name={v.whats ? 'toggleOn' : 'toggleOff'} /></button></div>
      <p className="m-hint">Se ela pedir para parar de receber mensagens, desligue aqui. Vale na hora.</p>
    </Sheet>
  );
}

// anamnese em 3 passos, com os cuidados aparecendo na hora
export function AnamneseSheet({ clientId }: { clientId: string }) {
  const nav = useNav();
  const toast = useToast();
  const refresh = useRefresh();
  const c = useClient(clientId).data;
  const [step, setStep] = useState(0);
  const [draft, setDraft] = useState<Answers | null>(null);
  const [consent, setConsent] = useState(false);
  if (!c) return <Sheet title="Anamnese"><Skeleton kind="sheet" rows={4} /></Sheet>;
  const prev = (c.anamnesis?.answers ?? null) as Answers | null;
  const d: Answers = draft ?? (() => {
    const o: Answers = { obs: (prev?.obs as string) ?? '' };
    AN_STEPS.forEach((st) => st.qs.forEach((q) => { o[q.k] = q.one ? ((prev?.[q.k] as string) ?? '') : [...((prev?.[q.k] as string[]) ?? [])]; }));
    return o;
  })();
  const set = (k: string, v: string | string[]) => setDraft({ ...d, [k]: v });
  const last = step === AN_STEPS.length - 1;
  async function save() {
    try {
      await api.post(`/clients/${clientId}/anamnesis`, { consent: true, answers: { ...d, obs: String(d.obs ?? '').trim() } });
      await refresh(); toast(`Anamnese da ${first(c!.name)} salva.`); nav.close();
    } catch (e) { toast(msg(e), 'warn'); }
  }
  return (
    <Sheet title={c.name} sub={c.anamnesis ? `Anamnese · preenchida em ${new Date(c.anamnesis.createdAt).toLocaleDateString('pt-BR')}` : 'Anamnese · nova'}
      actions={last
        ? <><button className="cta" disabled={!consent} onClick={save}>{consent ? 'Salvar anamnese' : 'Falta a autorização da cliente'}</button><button className="link-back" onClick={() => setStep(step - 1)}>Voltar</button></>
        : <><button className="cta" onClick={() => { setStep(step + 1); document.querySelector('.sheet-body')?.scrollTo(0, 0); }}>Continuar</button>{step > 0 ? <button className="link-back" onClick={() => setStep(step - 1)}>Voltar</button> : <button className="link-back" onClick={nav.close}>Voltar</button>}</>}>
      <div className="an-steps" aria-label={`Passo ${step + 1} de 3`}>{AN_STEPS.map((x, i) => <span key={x.t} className={i < step ? 'done' : i === step ? 'on' : ''}><i />{x.t}</span>)}</div>
      {step === 0 && <p className="m-hint an-intro">O que pede cuidado vira um aviso para quem atende a {first(c.name)}.</p>}
      {AN_STEPS[step]!.qs.map((q) => {
        const val = d[q.k];
        const arr = Array.isArray(val) ? val : [];
        const tips = q.one ? [] : arr.filter((o) => AN_CARE[o] && AN_CARE[o][0] !== 'info');
        return (
          <div key={q.k}>
            <p className="gl-title">{q.l}</p>
            {q.hint && <p className="m-hint an-hint">{q.hint}</p>}
            <div className="chipset soft">
              {q.one ? q.o.map((o) => <button key={o} className={val === o ? 'on' : ''} aria-pressed={val === o} onClick={() => set(q.k, val === o ? '' : o)}>{o}</button>) : (
                <>
                  <button className={!arr.length ? 'on' : ''} aria-pressed={!arr.length} onClick={() => set(q.k, [])}>{q.none}</button>
                  {q.o.map((o) => <button key={o} className={arr.includes(o) ? 'on' : ''} aria-pressed={arr.includes(o)} onClick={() => set(q.k, arr.includes(o) ? arr.filter((x) => x !== o) : [...arr, o])}>{o}</button>)}
                </>
              )}
            </div>
            {tips.length > 0 && <ul className="an-tips">{tips.map((o) => <li key={o} className={'lv-' + AN_CARE[o]![0]}><b>{AN_LVL[AN_CARE[o]![0]]}:</b> {AN_CARE[o]![1]}</li>)}</ul>}
          </div>
        );
      })}
      {last && (
        <>
          <p className="gl-title">Observações</p>
          <textarea className="an-obs" rows={3} value={String(d.obs ?? '')} onChange={(e) => set('obs', e.target.value)} placeholder="Ex.: prefere lixa fina, sente dor no dedão do pé" />
          <button className={'an-consent' + (consent ? ' on' : '')} role="checkbox" aria-checked={consent} onClick={() => setConsent(!consent)}>
            <i aria-hidden="true">{consent ? <Icon name="check" /> : null}</i>
            <span><b>{first(c.name)} autorizou guardar estas informações de saúde</b><small>{c.anamnesis ? `Autorização anterior registrada em ${new Date(c.anamnesis.consentAt).toLocaleDateString('pt-BR')}.` : 'Pela LGPD, dado de saúde só fica guardado com a autorização dela, só para cuidar dela no atendimento.'}</small></span>
          </button>
        </>
      )}
    </Sheet>
  );
}

// receber a pendência ("pagar depois")
export function DividaSheet({ clientId }: { clientId: string }) {
  const nav = useNav();
  const toast = useToast();
  const refresh = useRefresh();
  const me = useMe().data!;
  const c = useClient(clientId).data;
  const cash = useCash().data;
  const [method, setMethod] = useState<'pix' | 'cash' | 'debit' | 'credit'>('pix');
  const [given, setGiven] = useState('');
  if (!c) return <Sheet title="Receber pendência"><Skeleton kind="sheet" rows={3} /></Sheet>;
  const tot = c.debt;
  const g = given === '' ? tot : parseMoney(given);
  const okCash = method !== 'cash' || (!Number.isNaN(g) && g >= tot);
  const methods = (['pix', 'credit', 'debit', 'cash'] as const).filter((m) => me.salon.settings.methods[m]);
  const label = { pix: 'Pix', credit: 'Crédito', debit: 'Débito', cash: 'Dinheiro' };
  async function receive() {
    try {
      await api.post(`/clients/${clientId}/debt/pay`, { payments: [{ method, amountCents: tot }] });
      await refresh(); toast(`Pendência de ${first(c!.name)} recebida: ${money(tot)}.`); nav.close();
    } catch (e) { toast(msg(e), 'warn'); }
  }
  return (
    <Sheet title="Receber pendência" sub={`${c.name} · deve ${money(tot)}`}
      actions={<><button className="cta" disabled={!okCash || tot <= 0 || !cash?.session} onClick={receive}>Receber {money(tot)}</button>
        {c.whatsappConsentAt && <button className="link-back" onClick={() => window.open(`https://wa.me/55${c.phoneDisplay.replace(/\D/g, '')}?text=${encodeURIComponent(`Oi, ${first(c.name)}! Passando para lembrar do valor de ${money(tot)} que ficou para depois. Quando puder, é só acertar no próximo horário ou pelo Pix.`)}`, '_blank', 'noopener')}>Lembrar pelo WhatsApp</button>}</>}>
      <div className="gl"><Gr label="Em aberto" value={money(tot)} /></div>
      {!cash?.session && <div className="note warn"><Icon name="lock" /><span>Abra o caixa para receber.</span></div>}
      <div className="section-title">Forma de pagamento</div>
      <div className="methods grid3">{methods.map((m) => <button key={m} className={method === m ? 'sel' : ''} aria-pressed={method === m} onClick={() => setMethod(m)}>{label[m]}</button>)}</div>
      {method === 'cash' && <div className="field"><label htmlFor="dvCash">Quanto ela entregou</label><div className="money-in"><span>R$</span><input id="dvCash" inputMode="decimal" autoComplete="off" placeholder={(tot / 100).toFixed(2).replace('.', ',')} value={given} onChange={(e) => setGiven(e.target.value)} /></div></div>}
      {method === 'cash' && given !== '' && !Number.isNaN(g) && g > tot && <div className="live ok">Troco: {money(g - tot)}</div>}
    </Sheet>
  );
}
